import { useRef, useSyncExternalStore, useCallback, useEffect, useMemo } from "react";
function dispatchStorageEvent(key, newValue) {
  window.dispatchEvent(new StorageEvent("storage", { key, newValue }));
}
const setSessionStorageItem = (key, value) => {
  var _a;
  const stringifiedValue = typeof value === "object" ? JSON.stringify(value) : value;
  (_a = window.sessionStorage) == null ? void 0 : _a.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};
const removeSessionStorageItem = (key) => {
  var _a;
  (_a = window.sessionStorage) == null ? void 0 : _a.removeItem(key);
  dispatchStorageEvent(key, null);
};
const getSessionStorageItem = (key) => {
  var _a;
  return (_a = window.sessionStorage) == null ? void 0 : _a.getItem(key);
};
function createSessionStorageSubscribe(key) {
  return (callback) => {
    const handler = (e) => {
      if (!e.key || e.key === key) {
        callback(e);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  };
}
const getSessionStorageServerSnapshot = () => {
  return null;
};
function useSessionStorage(key, initialValue) {
  const lastValue = useRef(null);
  const getSnapshot = () => {
    const value2 = getSessionStorageItem(key);
    if (value2 === lastValue.current) return lastValue.current;
    lastValue.current = value2;
    return value2;
  };
  const store = useSyncExternalStore(
    createSessionStorageSubscribe(key),
    getSnapshot,
    getSessionStorageServerSnapshot
  );
  const setState = useCallback(
    (v) => {
      try {
        let nextState = v;
        if (typeof v === "function") {
          try {
            nextState = v(JSON.parse(store || ""));
          } catch (e) {
            console.warn(e);
            nextState = v(store || "");
          }
        }
        if (nextState === void 0 || nextState === null) {
          removeSessionStorageItem(key);
        } else {
          setSessionStorageItem(key, nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [key, store]
  );
  useEffect(() => {
    try {
      if (getSessionStorageItem(key) === null && typeof initialValue !== "undefined") {
        setSessionStorageItem(key, initialValue);
      }
    } catch (err) {
      console.warn(`useSessionStorage useEffect error: ${err}`);
    }
  }, [key, initialValue]);
  const value = useMemo(() => {
    try {
      let parsedStore = store ? JSON.parse(store) : initialValue;
      parsedStore = parsedStore !== void 0 && parsedStore !== null && typeof parsedStore !== "object" ? `${parsedStore}` : parsedStore;
      return parsedStore;
    } catch (err) {
      console.warn(`useSessionStorage parse error: ${err}`);
      return store || initialValue;
    }
  }, [store, initialValue]);
  try {
    return [value, setState];
  } catch (err) {
    console.error(`useSessionStorage error: ${err}`);
    return [initialValue, setState];
  }
}
export {
  useSessionStorage
};
